/* eslint-disable import/prefer-default-export */
import isArray from 'lodash/isArray';
import isObject from 'lodash/isObject';
import isUndefined from 'lodash/isUndefined';
import SCHEMAS from './schemas';
import { getFromBreadcrumbs, getImageUrl } from '../utils';

const {
  HIT_SCHEMA, NOTIFY_SCHEMA, RESERVATION_SCHEMA, SLOT_SCHEMA, VENUE_SCHEMA,
} = SCHEMAS;

const RESY_RATING_NAME = 'Resy';

function affiliatedRestaurantsTransform(source) {
  return source?.results?.venues.map((venue) => {
    const resyRating = venue?.rater?.find(((rater) => rater.name === RESY_RATING_NAME));

    return {
      id: venue.id.resy,
      image: getImageUrl(venue.responsive_images),
      name: venue.name,
      location: venue.location,
      cuisine: venue.type,
      collections: venue.collections,
      priceRange: venue.price_range_id,
      rating: resyRating?.score,
      totalRatings: resyRating?.total ?? null,
      urlSlug: venue.url_slug,
    };
  });
}

function autocompleteSearchTransform(source) {
  const {
    search: { hits },
    suggestions,
  } = source;

  const res = {
    facetValues: suggestions.map((suggestion) => ({ ...suggestion, facet: true })),
    hits: hits.map((hit) => transform(hit, HIT_SCHEMA.AUTOCOMPLETE)),
    meta: source.meta,
  };

  return res;
}

function finder4Transform(source) {
  const response = {
    query: source.query,
    results: {
      platinumNight: source.platinum_night,
      venues: [],
    },
  };

  response.results.venues = source.results.venues.map(
    (ven) => {
      const schema = {
        notifies: [NOTIFY_SCHEMA],
        slots: [SLOT_SCHEMA.FLY],
        templates: 'templates',
        venue: VENUE_SCHEMA.FINDER,
      };

      return transform(ven, schema);
    }
  );

  return response;
}

function inviteDetailsTransform(source) {
  const ven = transform(source.venue, VENUE_SCHEMA.INVITE);
  return { ...source, venue: ven };
}

function listVenuesTransform(source) {
  return source.map((ven) => transform(ven, VENUE_SCHEMA.LIST_VENUES));
}

function recommendedVenuesTransform(source) {
  return source?.map((venue) => {
    const transformedVenue = transform(venue, HIT_SCHEMA.SEARCH_RESULT);
    const slots = venue?.availability?.slots?.map((slot) => transform(slot, (SLOT_SCHEMA.SEARCH.FLY)));

    return {
      ...transformedVenue,
      slots,
    };
  });
}

function recommendationsTransform(source) {
  const response = {
    query: source.query,
    results: {
      venues: [],
    },
  };

  response.results.venues = source.results.map(
    (ven) => {
      const schema = {
        notifies: [NOTIFY_SCHEMA],
        slots: [SLOT_SCHEMA.FLY],
        templates: 'templates',
        venue: VENUE_SCHEMA.FINDER,
      };

      return transform(ven, schema);
    }
  );

  return response;
}

function reservationsTransform(source) {
  // Find and replace each reservation's venue id with the venue data
  return source?.reservations
    .map((reservation) => Object.assign(reservation, {
      currencyCode: reservation.venue.currency,
      venue: source.venues[reservation.venue.id],
    }))
    .map((reservation) => transform(reservation, RESERVATION_SCHEMA));
}

function suggestionsTransform(source) {
  return source.results.map((result) => {
    /* eslint-disable-next-line no-shadow */
    const { venue } = result;
    return {
      collections: venue.collections,
      cuisine: venue.type,
      id: venue.id.resy,
      location: {
        code: venue.location?.code,
        neighborhood: venue.location?.neighborhood,
        urlSlug: venue.location?.url_slug,
      },
      image: getImageUrl(venue.responsive_images),
      name: venue.name,
      priceRange: venue.price_range,
      rating: venue.rating,
      slots: result.slots,
      totalRatings: venue.total_ratings,
      urlSlug: venue.url_slug,
    };
  });
}

function venueTransform(source) {
  return transform(source, VENUE_SCHEMA.VENUE);
}

function venueSearchTransform(source) {
  const { total_pages: nbPages } = source.meta;
  const { hits } = source.search;
  const cuisines = source?.search?.cuisines ?? [];
  return {
    cuisines,
    results: [
      {
        hits: hits.map((hit) => {
          const transformedSearchResult = transform(hit, HIT_SCHEMA.SEARCH_RESULT);
          const activeNotificationsArray = Object.keys(transformedSearchResult.activeNotifications)
            .map((serviceTypeId) => ({
              [serviceTypeId]: transform(transformedSearchResult.activeNotifications[serviceTypeId], NOTIFY_SCHEMA),
            }));

          const activeNotifications = activeNotificationsArray.reduce((memo, notifies) => {
            const shiftKey = Object.keys(notifies)[0];
            memo[shiftKey] = notifies[shiftKey];
            return memo;
          }, {});

          const slots = hit.availability.slots.map((slot) => {
            const transformedSlot = transform(slot, (SLOT_SCHEMA.SEARCH.FLY));
            const gatingErrors = hit.availability?.gating_errors ?? {};
            const templateId = slot?.template?.id;
            const hasMenus = templateId && !!(hit.availability?.templates?.[templateId]?.menu_ids?.length);

            return {
              ...transformedSlot,
              gatingErrors,
              hasMenus,
            };
          });

          return {
            ...transformedSearchResult,
            activeNotifications,
            tockNotifyUrl: hit?.availability?.tock_notify_url ?? null,
            experiences: isArray(transformedSearchResult.experiences) ? transformedSearchResult.experiences : [],
            slots,
          };
        }),
        nbPages,
      },
    ],
    meta: source.meta,
  };
}

function surveyQuestionsTransform(source) {
  const questionsets = source?.questionsets ?? [];
  const questions = questionsets.flatMap((questionset) => {
    const qs = Array.isArray(questionset.questions) ? questionset.questions : [];
    const formatedQuestions = qs.map((q) => {
      const s = {
        ...q,
        ...q.question,
        options: (q.question?.options || []).map((opt) => ({ option: opt })),
      };
      return transform(s, SCHEMAS.SURVEY_SCHEMA.QUESTION);
    });
    formatedQuestions.sort((a, b) => Number(a.order ?? 0) - Number(b.order ?? 0));
    return formatedQuestions;
  });
  return questions;
}

function transform(source, schema, response) {
  let localResponse = response;
  if (isUndefined(localResponse)) {
    localResponse = {};
  }

  Object.keys(schema).forEach((key) => {
    if (isArray(schema[key])) {
      localResponse[key] = source[key].map((element) => transform({ ...element, ...source }, schema[key][0]));
    } else if (isObject(schema[key]) && !('_modifier' in schema[key])) {
      localResponse[key] = {};
      const obj = source[key];
      transform({ ...source, ...obj }, schema[key], localResponse[key]);
    } else {
      let value;
      let modifier;

      if (isObject(schema[key]) && '_modifier' in schema[key]) {
        // eslint-disable-next-line no-underscore-dangle
        modifier = schema[key]._modifier;
        value = getFromBreadcrumbs(source, schema[key].breadcrumbs);
      } else {
        value = getFromBreadcrumbs(source, schema[key]);
      }

      if (!isUndefined(modifier)) {
        if (isArray(modifier)) {
          modifier.forEach((mod) => {
            if (value == null) return;
            if (typeof mod.fn !== 'function') return;
            if (mod.needsSource) {
              value = mod.fn(source, mod.key, value);
            } else {
              value = mod.fn(value, mod.key);
            }
          });

          localResponse[key] = value;
        } else {
          localResponse[key] = modifier(value);
        }
      } else {
        localResponse[key] = value;
      }
    }
  });

  return localResponse;
}

export {
  affiliatedRestaurantsTransform,
  autocompleteSearchTransform,
  finder4Transform,
  inviteDetailsTransform,
  listVenuesTransform,
  reservationsTransform,
  recommendedVenuesTransform,
  recommendationsTransform,
  suggestionsTransform,
  venueTransform,
  venueSearchTransform,
  surveyQuestionsTransform,
};
